<template>
    <div class="counter-cell">
        <v-row
            v-if="editMode && !disabled"
            no-gutters
            align="center"
            justify="center"
            class="headline text-none fill-height flex-nowrap"
            :class="{ 'in-focus': inFocus }"
        >
            <v-col cols="10" class="d-flex flex-row align-center" :class="{ 'justify-center': inline }">
                <v-text-field
                    ref="inputField"
                    v-model="model"
                    :data-id="`counter-cell-${riderType}-${suffix}`"
                    class="pr-1 d-block"
                    type="number"
                    min="0"
                    max="99"
                    step="1"
                    flat
                    @change="updateValue(model)"
                    @focus="onFocus"
                    @blur="inFocus = false"
                />
                <rider-icon :text="iconText" :icon-size="24" :size-height="40">
                    {{ icon }}
                </rider-icon>
                <span v-if="suffix" class="pl-1">{{ suffix }}</span>
            </v-col>
        </v-row>
        <v-btn
            v-else
            large
            elevation="0"
            block
            height="63"
            class="headline text-none"
            :disabled="disabled"
            variant="text"
            :data-id="`update-count-${riderType}-${suffix}`"
            @click="updateValue(count + 1)"
        >
            <div class="d-flex flex-row align-center w-50" :class="{ 'justify-center': inline }">
                <span class="pr-2">{{ count }}</span>
                <rider-icon :text="iconText" :icon-size="24" :size-height="34">
                    {{ icon }}
                </rider-icon>
                <span v-if="suffix" class="pl-1">{{ suffix }}</span>
            </div>
        </v-btn>
    </div>
</template>

<script lang="ts" setup>
import { RiderTypes } from '@/features/shared/models/RiderType';
import RiderIcon from '@/features/shared/RiderIcon.vue';
import { ref, computed, watch } from 'vue';
import { getIcon, getIconText } from '@/helpers/IconHelpers';

const props = withDefaults(
    defineProps<{
        count: number;
        riderType: string;
        suffix?: string;
        editMode: boolean;
        disabled: boolean;
        inline: boolean;
    }>(),
    {
        count: 0,
        riderType: RiderTypes.Passengers,
        editMode: false,
        disabled: false,
        inline: true
    }
);

const emit = defineEmits(['input']);

const model = ref('');
const inFocus = ref(false);
const inputField = ref(null);

model.value = String(props.count);

const icon = computed(() => {
    return getIcon(props.riderType);
});

const iconText = computed(() => {
    return getIconText(props.riderType);
});

function updateValue(value: string | number) {
    emit('input', Number(value));
}

function onFocus(event) {
    inFocus.value = true;
    event.target.select();
}

watch(
    () => props.count,
    (value: number) => {
        model.value = String(value);
    }
);
</script>

<style lang="scss" scoped>
.counter-cell {
    height: 63px;

    .in-focus {
        border: 2px solid green;
    }

    ::v-deep .v-text-field {
        max-width: 45px;

        .v-text-field__slot input {
            text-align: center;
        }
    }

    .rider-icon {
        font-size: 24px;
        transform: rotate(0.03deg);
    }

    .v-btn .rider-icon {
        font-size: 24px;
    }

    .headline {
        overflow: hidden;
    }
}
</style>

<style lang="scss">
.counter-cell {
    font-size: 20px;
    font-weight: 400;

    .v-btn {
        font-size: 20px;
        font-weight: 400;
        letter-spacing: normal;
    }

    .v-field__input {
        padding-right: 0;
        padding-left: 4px;
        padding-bottom: 4px;
        text-align: center;
        background: #ffffff;
        font-size: 1.5rem;
    }
}
</style>
